import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MobileMultiSelect } from '@shared/components/filter/filterOptions/MobileMultiSelect';
import {
  MobileTagSelections,
  RenderItem,
} from '@shared/components/filter/filterOptions/MobileTagOptions';
import {
  ControlOption,
  FilterOperation,
  FilterOption,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
  FilterTagSelection,
  isFilterTagSelectionList,
  tagSelectionToTagOption,
} from '@shared/components/filter/types';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { uuid } from '@shared/util/uuid';
import { useConditionTypeOptions } from './hooks/useConditionTypeOptions';
import { AutoExtendConditionType, AutoExtendOptionsProps, RepeatEndType } from './types';
import { MAX_COUNT, getInitialAutoExtendTagFilterOperator, getOperatorLabel } from './utils';

const MIN_COUNT = 1;

export function AutoExtendOptionsMobile({
  minimumInSeries,
  setMinimumInSeries,
  tagFilter,
  setTagFilter,
  component,
  workflowTemplateId,
  showingTagFilterOptions = false,
  setShowingTagFilterOptions,
  conditionType,
  setConditionType,
  setRepeatEndType,
}: AutoExtendOptionsProps) {
  const hasDefaultAutoExtend =
    !isNil(component?.autoExtendDefault?.minimumInSeries) &&
    !isNil(component?.autoExtendDefault?.impliedFilterComponentId);

  const conditionTypeOptions = useConditionTypeOptions({ component });
  const [selectedOperator, setSelectedOperator] = useState<string>(
    getInitialAutoExtendTagFilterOperator(tagFilter),
  );

  // Handle selecting or deselecting a condition type option
  const handleConditionTypeSelect = useCallback(
    (selectedType: AutoExtendConditionType) => {
      const nextType: AutoExtendConditionType | undefined =
        conditionType === selectedType ? undefined : selectedType;

      setConditionType(nextType);

      if (nextType === 'default') {
        setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? undefined);
        setTagFilter({
          componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
          value: {
            containsString: null,
            isEmpty: null,
            stringWildcard: null,
            containsNumber: null,
            dateIs: null,
            dateRange: null,
            dateTokenIs: null,
            numberIs: null,
            containsCurrentUser: null,
          },
        });
        setRepeatEndType(RepeatEndType.NEVER);
      } else if (nextType === 'custom') {
        setMinimumInSeries(MAX_COUNT);
        setTagFilter(undefined);
      } else {
        // Clearing – remove all values
        setMinimumInSeries(undefined);
        setTagFilter(undefined);
        setRepeatEndType(RepeatEndType.END_DATE);
      }
    },
    [
      conditionType,
      component,
      setMinimumInSeries,
      setTagFilter,
      setConditionType,
      setRepeatEndType,
    ],
  );

  useEffect(() => {
    if (conditionTypeOptions.length === 1) {
      const onlyOpt = conditionTypeOptions[0].id as AutoExtendConditionType;
      if (conditionType !== onlyOpt) {
        setConditionType(onlyOpt);
      }
    }
  }, [conditionTypeOptions, conditionType, setConditionType]);

  const countIsOutOfRange =
    isNil(minimumInSeries) || minimumInSeries < MIN_COUNT || minimumInSeries > MAX_COUNT;

  const handleMinimumInSeriesChange = (newCount: string) => {
    if (newCount === '') {
      setMinimumInSeries(undefined);
      return;
    }
    const num = Number(newCount);
    if (!isNaN(num)) {
      setMinimumInSeries(num);
    }
  };

  const label = conditionTypeOptions.find(o => o.id === conditionType)?.label;
  const placeholder = i18n(
    'workflowTemplate.component.dateAndRepeat.autoExtend.setConditionPlaceholder',
  );

  return (
    <View paddingTop={1.5}>
      {!showingTagFilterOptions && (
        <View marginBottom={1.5}>
          <Text size="body1" weight="semibold">
            {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.title')}
          </Text>
        </View>
      )}
      {hasDefaultAutoExtend && !showingTagFilterOptions && (
        <View marginBottom={conditionType === 'custom' ? 1.5 : 0}>
          <Menu>
            <Menu.Button label={label ?? placeholder} />
            <Menu.Content>
              <Menu.Options
                options={conditionTypeOptions}
                onSelect={option => handleConditionTypeSelect(option.id as AutoExtendConditionType)}
                getItemKey={option => option.id}
                getItemLabel={option => option.label ?? ''}
                isItemSelected={option => option.id === conditionType}
              />
            </Menu.Content>
          </Menu>
        </View>
      )}

      {conditionType === 'custom' && !showingTagFilterOptions && (
        <>
          <View>
            <View marginBottom={1.5}>
              <View row centerVertically>
                <MBTextInput
                  value={String(minimumInSeries ?? '')}
                  onChangeText={handleMinimumInSeriesChange}
                  style={[
                    {
                      width: grid(7),
                      height: grid(5),
                      paddingVertical: 0,
                      paddingHorizontal: grid(1),
                      backgroundColor: Colors.v2.backgroundGrayLight,
                      borderWidth: 0,
                      marginRight: grid(1),
                    },
                    countIsOutOfRange && { borderWidth: 1, borderColor: Colors.v2.red },
                  ]}
                  placeholder="1-100"
                />
                <Text size="body1">
                  {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.minimumInSeries', {
                    count: minimumInSeries ?? 0,
                  })}
                </Text>
              </View>
            </View>
          </View>
          <TouchableOpacity
            style={{
              paddingVertical: grid(0.5),
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            onPress={() => setShowingTagFilterOptions?.(true)}
          >
            <View
              row
              centerVertically
              style={{
                width: '85%',
                justifyContent: 'flex-start',
              }}
            >
              <TagFilterSummary
                tagFilter={tagFilter}
                workflowTemplateId={workflowTemplateId}
                selectedOperator={selectedOperator}
              />
            </View>
            <MBIconV2 name="arrow-point-right" color={Colors.v2.purple} size={grid(3.5)} />
          </TouchableOpacity>
        </>
      )}

      {conditionType === 'custom' && showingTagFilterOptions && (
        <TagFilter
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          workflowTemplateId={workflowTemplateId}
          selectedOperator={selectedOperator}
          setSelectedOperator={setSelectedOperator}
        />
      )}
    </View>
  );
}

function TagFilter({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
  selectedOperator,
  setSelectedOperator,
}: {
  tagFilter?: API.FilterByComponentFragment;
  setTagFilter: (tagFilter?: API.FilterByComponentFragment) => void;
  workflowTemplateId: string;
  selectedOperator: string;
  setSelectedOperator: (operator: string) => void;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const operatorLabel = getOperatorLabel(selectedOperator);
  const tagComponents = useMemo(
    () => (workflowTemplate?.components ?? []).filter(isTagComponent) as API.TagComponent[],
    [workflowTemplate],
  );

  const tagComponentOptions: ControlOption[] = tagComponents.map(tc => ({
    id: tc.id,
    label: tc.label ?? '',
    icon: tc.icon as MBIconV2Name,
  }));

  const selectedTagComponentOption = tagComponentOptions.find(o => o.id === tagFilter?.componentId);
  const selectedTagComponent = useMemo(() => {
    return workflowTemplate?.components.find(c => c.id === tagFilter?.componentId) as
      | API.TagComponent
      | undefined;
  }, [workflowTemplate, tagFilter]);

  const isMultiSelect = selectedTagComponent?.multiselect ?? false;

  const filterUuidRef = useRef<string>(uuid());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }

    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label ?? '',
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    };
  }, [selectedTagComponent]);

  /**
   * Build the `selectedOptions` map expected by `MobileTagSelections`. The key MUST match
   * `filterOption.uuid` so the component can locate selections.
   */
  const selectedOptions = useMemo(() => {
    const map = new Map<string, FilterSelectionState>();

    if (!filterOption) {
      return map;
    }

    let selections: FilterTagSelection[] = [];

    const tagValues = tagFilter?.value?.containsString?.values;

    if (tagValues && tagValues.length > 0) {
      selections = tagValues.map(tv => ({
        type: 'TAG',
        filter: filterOption as FilterOption,
        value: String(tv),
      }));
    }

    map.set(filterOption.uuid!, {
      filter: filterOption,
      selection: selections,
      negate: selectedOperator === 'does-not-contain',
    });

    return map;
  }, [filterOption, tagFilter, selectedOperator]);

  const handleSetOption = (
    _filter: ControlOption,
    operation: FilterOperation,
    selection?: FilterSelection,
  ) => {
    if (operation !== 'updateSelections' || !filterOption) {
      return;
    }

    const newFilterState: FilterSelectionState = {
      filter: filterOption,
      selection: (selection ?? []) as FilterSelection,
      negate: selectedOperator === 'does-not-contain',
    };

    const newTagFilter = getFilterComponent(newFilterState);
    if (newTagFilter) {
      setTagFilter(newTagFilter);
    }
  };

  const handleTagComponentOptionChange = (option: ControlOption) => {
    if (option.id === tagFilter?.componentId) {
      setTagFilter(undefined);
      return;
    }

    const newFilter: API.FilterByComponentFragment = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as API.FilterByComponentFragment;

    setTagFilter(newFilter);
  };

  const handleClearAllOptions = () => {
    if (!tagFilter) {
      return;
    }
    const clearedFilter: API.FilterByComponentFragment = {
      componentId: tagFilter.componentId,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    };
    setTagFilter(clearedFilter);
  };

  const rawSelections: FilterSelection | undefined = !isNil(filterOption?.uuid)
    ? selectedOptions.get(filterOption.uuid)?.selection
    : undefined;

  const tagComponent =
    !isNil(filterOption?.component) && isTagComponent(filterOption?.component)
      ? filterOption?.component
      : undefined;

  const tagSelections: FilterTagSelection[] =
    rawSelections && isFilterTagSelectionList(rawSelections) ? rawSelections : [];

  const selectedTagOptions: FilterTagOption[] = tagSelections.map(tagSelectionToTagOption);
  const availableTagOptions: FilterTagOption[] = (tagComponent?.options ?? []).map(o => ({
    value: o.value,
    label: o.label ?? '',
    backgroundColor: o.backgroundColor as FilterTagOption['backgroundColor'],
    archived: o.archived ?? undefined,
  }));

  const operatorOptions: ControlOption[] = [
    { id: 'contains', label: i18n('card.actions.filters.fields.common.operator.autoContains') },
    {
      id: 'does-not-contain',
      label: i18n('card.actions.filters.fields.common.operator.doesNotContainMenu'),
    },
  ];

  const handleOperatorSelect = (option: ControlOption) => {
    if (option.id === selectedOperator || !filterOption) {
      return;
    }
    setSelectedOperator(option.id);
    handleSetOption(
      filterOption,
      'updateSelections',
      option.id === 'contains' ? tagSelections : undefined,
    );
  };

  return (
    <View gap={1}>
      <Menu>
        <Menu.Button
          flex
          icon={selectedTagComponentOption?.icon}
          label={selectedTagComponentOption?.label ?? i18n('card.actions.groups.noneSelected')}
        />
        <Menu.Content>
          <Menu.Options
            options={tagComponentOptions}
            onSelect={handleTagComponentOptionChange}
            getItemKey={option => option.id}
            getItemLabel={option => option.label}
            getItemIcon={option => option.icon}
            isItemSelected={option => option.id === tagFilter?.componentId}
          />
        </Menu.Content>
      </Menu>
      {filterOption && (
        <Menu>
          <Menu.Button flex label={operatorLabel} />
          <Menu.Content>
            <Menu.Options
              options={operatorOptions}
              onSelect={handleOperatorSelect}
              getItemKey={option => option.id}
              getItemLabel={option => option.label}
              isItemSelected={option => option.id === selectedOperator}
            />
          </Menu.Content>
        </Menu>
      )}
      {filterOption && selectedOperator === 'does-not-contain' && (
        <MobileMultiSelect
          onChange={selected =>
            handleSetOption(
              filterOption,
              'updateSelections',
              selected.map(({ value }) => ({
                type: 'TAG',
                filter: filterOption,
                value: String(value),
              })),
            )
          }
          onDeselectAll={() => handleClearAllOptions()}
          selectedOptions={selectedTagOptions}
          options={availableTagOptions}
          paddingHorizontal={0}
          renderItem={({ data }) => <RenderItem data={data} />}
          isMultiSelect={isMultiSelect}
        />
      )}
    </View>
  );
}

// Summary row component to display selected tag filter info when options sheet is closed
function TagFilterSummary({
  tagFilter,
  workflowTemplateId,
  selectedOperator,
}: {
  tagFilter?: API.FilterByComponentFragment;
  workflowTemplateId: string;
  selectedOperator: string;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const selectedTagComponent = useMemo(() => {
    return workflowTemplate?.components.find(c => c.id === tagFilter?.componentId) as
      | API.TagComponent
      | undefined;
  }, [workflowTemplate, tagFilter]);

  const label =
    selectedTagComponent?.label ??
    i18n('workflowTemplate.component.dateAndRepeat.autoExtend.noneSelectedTagFilterLabel');

  const tagValues = tagFilter?.value?.containsString?.values;
  const isNegated = tagFilter?.value?.containsString?.negate;
  const hasTagsSelected = !isEmpty(tagValues);

  // Build a FilterOption representing the selected tag component so we can reuse
  // existing utility components like `MobileTagSelections` for display.
  const filterUuidRef = useRef<string>(uuid());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }

    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label ?? '',
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    };
  }, [selectedTagComponent]);

  /**
   * Build the `selectedOptions` map expected by `MobileTagSelections`. The key MUST match
   * `filterOption.uuid` so the component can locate selections.
   */
  const selectedOptions = useMemo(() => {
    const map = new Map<string, FilterSelectionState>();

    if (!filterOption) {
      return map;
    }

    let selections: FilterTagSelection[] = [];

    if (tagValues && tagValues.length > 0) {
      selections = tagValues.map(tv => ({
        type: 'TAG',
        filter: filterOption as FilterOption,
        value: String(tv),
      }));
    }

    map.set(filterOption.uuid!, {
      filter: filterOption,
      selection: selections,
      negate: selectedOperator === 'does-not-contain',
    });

    return map;
  }, [filterOption, tagValues, selectedOperator]);

  const hasSelections = hasTagsSelected;

  return (
    <View style={{ maxWidth: '85%', flexShrink: 0, flexGrow: 0, flexBasis: '100%' }}>
      {!hasSelections && !filterOption && (
        <Text size="body1" inactive>
          {label}
        </Text>
      )}
      {filterOption && (
        <View style={{ width: '100%' }} marginBottom={0.5}>
          <Text size="overline">{label}</Text>
        </View>
      )}
      {filterOption && (
        <View row centerVertically>
          <View marginRight={1}>
            <Text size="body1" inactive>
              {!isNegated && i18n('card.actions.filters.fields.common.operator.autoContains')}
              {isNegated &&
                i18n('card.actions.filters.fields.common.operator.doesNotContainSelected')}
            </Text>
          </View>
          {isNegated && (
            <MobileTagSelections filter={filterOption} selectedOptions={selectedOptions} />
          )}
        </View>
      )}
    </View>
  );
}
